import React, { useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { useUsersAPI } from "../../hooks/api/use-usersAPI";
import { useGetQuery } from "../../store/slices/apiSlice";
import { usePasswordValidation } from "../../hooks/usePasswordValidation";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";
import CustomInputText from "../../components/CustomInput/CustomInputText";
import CustomPassword from "../../components/CustomInput/CustomPassword";
import {
  loadingPage,
  invalidLinkTemplate,
  userNotFoundTemplate,
  successTemplate,
} from "../../components/LoginFormTemplates/LoginFormTemplates";
import FloatingLogo from "../../components/FloatingLogo/FloatingLogo";
import { H2, DangerText, MediumText } from "../../styles/Typography";
import { Column, Container, InputFlex } from "./ActivateUser.styles";
import { IconMail } from "../../assets/icons/Icon";

const ActivateUser: React.FC = () => {
  const { usersEP, handleActivateUser, toastRef } = useUsersAPI();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const userId = searchParams.get("user");

  const [email, setEmail] = useState<string>("");
  const copyToastRef = useRef<Toast>(null);

  const { data: user, isLoading } = useGetQuery(
    `${usersEP}/activate?userId=${userId}&token=${token}`,
    {
      refetchOnMountOrArgChange: true,
      skip: !token || !userId || !usersEP,
    }
  );

  useEffect(() => {
    if (user) setEmail(user.email);
  }, [user]);

  const {
    password,
    loading,
    isSuccess,
    handleNewPasswordChange,
    handleConfirmPasswordChange,
    handleBlur,
    getPasswordError,
    getConfirmError,
    handleSubmit,
  } = usePasswordValidation(handleActivateUser);

  if (isLoading) return loadingPage();
  if (!token || !userId) return invalidLinkTemplate(copyToastRef, "активации");
  if (!user) return userNotFoundTemplate(copyToastRef);
  if (isSuccess) return successTemplate("Ваш аккаунт активирован.");

  return (
    <Column>
      <Container>
        <Toast ref={toastRef} position="bottom-center" />
        <FloatingLogo />
        <H2>
          Добро пожаловать, <br />
          {user?.lastName} {user?.name}
        </H2>
        <InputFlex>
          <CustomInputText
            value={email}
            setValue={() => undefined}
            placeholder="Почта"
            icon={<IconMail size={20} />}
            disabled={true}
          />
        </InputFlex>
        <MediumText>Придумайте пароль для своего аккаунта</MediumText>
        <InputFlex>
          {getPasswordError() && <DangerText>{getPasswordError()}</DangerText>}
          <CustomPassword
            value={password.password}
            setValue={handleNewPasswordChange}
            placeholder="Новый пароль"
            invalid={!!getPasswordError()}
            onBlur={() => handleBlur("password")}
          />
        </InputFlex>
        <InputFlex>
          {getConfirmError() && <DangerText>{getConfirmError()}</DangerText>}
          <CustomPassword
            value={password.confirm}
            setValue={handleConfirmPasswordChange}
            placeholder="Подтвердите пароль"
            invalid={!!getConfirmError()}
            onBlur={() => handleBlur("confirm")}
          />
        </InputFlex>
        <Button
          label="Сохранить"
          onClick={() => handleSubmit(token ?? "", userId ?? "")}
          loading={loading}
        />
      </Container>
    </Column>
  );
};

export default ActivateUser;
